import {
    Alert,
    Button,
    Group,
    Loader,
    Modal,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
} from '@mantine/core'
import {notifications} from '@mantine/notifications'
import {useNavigate} from '@tanstack/react-router'
import {format} from 'date-fns'
import {type ClipboardEvent, useState} from 'react'

import {businessCalendarDate} from 'src/api/business-time'
import {useGeocoder} from 'src/jobs/google-maps/geocode-address'
import {formatJobDate, toApiDateString} from 'src/jobs/job-schedule-format'
import type {JobDetail} from 'src/jobs/schemas'
import {useJob} from 'src/jobs/use-jobs'
import {createUuid} from 'src/lib/create-uuid'
import {isRealSite} from 'src/lib/texas-bounds'
import {useCreateProjectJobsBatch} from 'src/projects/use-projects'
import {OutOfStateBadge} from 'src/ui/out-of-state-badge'

import {
    baseJobName,
    cellProblem,
    describeCounts,
    emptyRow,
    isBlankRow,
    MAX_SITES,
    pasteIntoRows,
    type Place,
    placeFromRow,
    placeOutOfState,
    rowDays,
    type RowErrors,
    rowInPast,
    siteColumns,
    type SiteRow,
    toSiteJobInput,
    typedOutOfState,
    validateSiteRows,
} from './more-sites-logic'

const STARTING_ROWS = 3

type MoreSitesPageProps = {
    projectId: string
    orderLabel: string
    sourceJobId: string
    readOnly?: boolean
}

/** Bulk-create copies of a job at multiple sites from table or pasted rows. */
export function MoreSitesPage({
    projectId,
    orderLabel,
    sourceJobId,
    readOnly = false,
}: MoreSitesPageProps) {
    const {data: job, isLoading, isError} = useJob(sourceJobId)
    if (isLoading) {
        return <Loader aria-label="Loading the job" />
    }
    if (isError || !job) {
        return <Alert color="red">Could not load source job</Alert>
    }

    return (
        <MoreSitesTable
            key={job.id}
            projectId={projectId}
            orderLabel={orderLabel}
            job={job}
            readOnly={readOnly}
        />
    )
}

type PendingSite = {row: SiteRow; place: Place}

function MoreSitesTable({
    projectId,
    orderLabel,
    job,
    readOnly,
}: {
    projectId: string
    orderLabel: string
    job: JobDetail
    readOnly: boolean
}) {
    const navigate = useNavigate()
    const createJobs = useCreateProjectJobsBatch(projectId)
    const {geocodeAddress, isReady: canLookUp} = useGeocoder()
    const {columns, lines} = siteColumns(job)
    const [rows, setRows] = useState<SiteRow[]>(() => Array.from({length: STARTING_ROWS}, emptyRow))
    const [errors, setErrors] = useState<RowErrors>({})
    const [notice, setNotice] = useState<string | null>(null)
    const [checking, setChecking] = useState(false)
    const [pending, setPending] = useState<PendingSite[] | null>(null)
    // Idempotency key for the batch create request.
    const [requestId, setRequestId] = useState(createUuid)
    const [saveError, setSaveError] = useState<string | null>(null)
    const today = businessCalendarDate()
    const firstDay = rowDays(emptyRow(), job, today).start
    const filledCount = rows.filter((row) => !isBlankRow(row)).length

    function checkCell(rowIndex: number, key: string) {
        const row = rows[rowIndex]
        if (!row) {
            return
        }

        const problem = isBlankRow(row) ? null : cellProblem(row, key, lines, today)
        const rest = Object.fromEntries(
            Object.entries(errors[rowIndex] ?? {}).filter(([column]) => column !== key),
        )
        setErrors({...errors, [rowIndex]: problem ? {...rest, [key]: problem} : rest})
    }

    function setCell(rowIndex: number, key: string, value: string) {
        setRows((current) =>
            current.map((row, index) => (index === rowIndex ? {...row, [key]: value} : row)),
        )
        if (errors[rowIndex]?.[key]) {
            setErrors({
                ...errors,
                [rowIndex]: Object.fromEntries(
                    Object.entries(errors[rowIndex]).filter(([column]) => column !== key),
                ),
            })
        }
    }

    function handlePaste(
        event: ClipboardEvent<HTMLInputElement>,
        rowIndex: number,
        columnKey: string,
    ) {
        const text = event.clipboardData.getData('text')
        if (!/[\t\n]/.test(text.trim())) {
            return
        }

        event.preventDefault()
        const {
            rows: nextRows,
            pasted,
            cut,
        } = pasteIntoRows(rows, text, columns, rowIndex, columnKey)
        setNotice(
            cut
                ? `Only the first ${MAX_SITES} sites were pasted`
                : `Pasted ${pasted} ${pasted === 1 ? 'row' : 'rows'}.`,
        )
        setRows(nextRows)
        setErrors(validateSiteRows(nextRows, lines, today).errors)
    }

    async function handleCheck() {
        setNotice(null)
        const {errors: found, filled} = validateSiteRows(rows, lines, today)
        if (filled.length === 0) {
            setNotice('At least one site is required')
            return
        }
        const sites: PendingSite[] = []
        const lookUpErrors: RowErrors = {...found}
        setChecking(true)
        try {
            for (const index of filled) {
                const row = rows[index]
                if (!row || found[index]?.['place']) {
                    continue
                }
                const typed = placeFromRow(row)
                if (typed) {
                    sites.push({row, place: typed})
                    continue
                }
                const address = (row['place'] ?? '').trim()
                const result = canLookUp ? await geocodeAddress(address) : null
                if (result && !isRealSite(result.latitude, result.longitude)) {
                    lookUpErrors[index] = {
                        ...lookUpErrors[index],
                        place: 'Address is outside the continental US',
                    }
                } else if (result) {
                    sites.push({
                        row,
                        place: {
                            latitude: result.latitude,
                            longitude: result.longitude,
                            fullAddress: result.formattedAddress,
                        },
                    })
                } else {
                    lookUpErrors[index] = {
                        ...lookUpErrors[index],
                        place: canLookUp
                            ? 'Address not found. Enter lat, long'
                            : 'Address lookup unavailable. Enter lat, long',
                    }
                }
            }
        } finally {
            setChecking(false)
        }
        setErrors(lookUpErrors)
        if (Object.keys(lookUpErrors).length > 0) {
            const count = Object.keys(lookUpErrors).length
            setNotice(`${count} ${count === 1 ? 'row needs' : 'rows need'} fixing`)
            return
        }
        setRequestId(createUuid())
        setPending(sites)
    }

    async function handleCreate(confirmed: PendingSite[]) {
        setSaveError(null)
        try {
            const created = await createJobs.mutateAsync({
                inputs: confirmed.map(({row, place}) =>
                    toSiteJobInput(row, place, job, lines, today),
                ),
                requestId,
            })
            notifications.show({
                color: 'green',
                title: `${created.length} ${created.length === 1 ? 'job' : 'jobs'} added to order ${orderLabel}`,
                message: 'Dispatch notified.',
            })
            await navigate({to: '/projects/$projectId', params: {projectId}})
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'Failed to create jobs')
        }
    }

    return (
        <Stack gap="md">
            <Alert color="blue" variant="light">
                Each row is a copy of job {job.displayId} {baseJobName(job)}. Blank cells use the
                job counts and {formatJobDate(toApiDateString(firstDay))}.
            </Alert>
            {notice ? (
                <Text
                    size="sm"
                    c={notice.includes('fix') ? 'red' : 'dimmed'}
                    data-testid="more-sites-notice"
                >
                    {notice}
                </Text>
            ) : null}
            <ScrollArea>
                <Table withTableBorder withColumnBorders miw={720}>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th w={36}>#</Table.Th>
                            {columns.map((column) => (
                                <Table.Th key={column.key}>
                                    {column.label}
                                    {'defaultQuantity' in column ? (
                                        <Text span size="xs" c="dimmed">
                                            {' '}
                                            ({column.defaultQuantity})
                                        </Text>
                                    ) : null}
                                </Table.Th>
                            ))}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rows.map((row, rowIndex) => (
                            <Table.Tr key={rowIndex}>
                                <Table.Td>
                                    <Text size="xs" c="dimmed">
                                        {rowIndex + 1}
                                    </Text>
                                </Table.Td>
                                {columns.map((column) => (
                                    <Table.Td key={column.key}>
                                        <TextInput
                                            size="xs"
                                            aria-label={`Row ${rowIndex + 1} ${column.label}`}
                                            placeholder={
                                                'defaultQuantity' in column
                                                    ? String(column.defaultQuantity)
                                                    : column.key === 'date'
                                                      ? formatJobDate(toApiDateString(firstDay))
                                                      : column.key === 'place'
                                                        ? '29.88, -94.92'
                                                        : ''
                                            }
                                            value={row[column.key] ?? ''}
                                            error={errors[rowIndex]?.[column.key]}
                                            onChange={(event) =>
                                                setCell(
                                                    rowIndex,
                                                    column.key,
                                                    event.currentTarget.value,
                                                )
                                            }
                                            onBlur={() => checkCell(rowIndex, column.key)}
                                            onPaste={(event) =>
                                                handlePaste(event, rowIndex, column.key)
                                            }
                                        />
                                        {column.key === 'place' && !errors[rowIndex]?.['place'] ? (
                                            <OutOfStateBadge mt={4} label={typedOutOfState(row)} />
                                        ) : null}
                                    </Table.Td>
                                ))}
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
            <Group justify="space-between">
                <Group gap="sm">
                    <Button
                        variant="subtle"
                        onClick={() => void navigate({to: '/jobs/$jobId', params: {jobId: job.id}})}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="default"
                        disabled={rows.length >= MAX_SITES}
                        onClick={() => setRows([...rows, emptyRow()])}
                    >
                        Add row
                    </Button>
                </Group>
                <Button
                    loading={checking}
                    disabled={filledCount === 0 || readOnly}
                    onClick={() => void handleCheck()}
                >
                    Create {filledCount} {filledCount === 1 ? 'job' : 'jobs'}
                </Button>
            </Group>
            <Modal
                opened={pending != null}
                onClose={() => setPending(null)}
                title={`Create ${pending?.length ?? 0} ${pending?.length === 1 ? 'job' : 'jobs'} in order ${orderLabel}?`}
                size="lg"
            >
                {pending ? (
                    <Stack gap="md">
                        <Text size="sm" c="dimmed">
                            All jobs are created together or none are.
                        </Text>
                        <ScrollArea.Autosize mah={360}>
                            <Stack
                                gap={4}
                                component="ol"
                                style={{paddingLeft: 20, margin: 0}}
                                data-testid="more-sites-confirm-list"
                            >
                                {pending.map(({row, place}, index) => {
                                    const {start} = rowDays(row, job, today)
                                    const outOfState = placeOutOfState(place)
                                    return (
                                        <Text key={index} size="sm" component="li">
                                            {row['site']?.trim()} · {describeCounts(row, lines)} ·{' '}
                                            {format(start, 'EEE MMM d, yyyy')}
                                            {rowInPast(row, job, today) ? (
                                                <Text span size="sm" c="orange" fw={500}>
                                                    {' '}
                                                    (in the past)
                                                </Text>
                                            ) : null}
                                            {outOfState ? (
                                                <>
                                                    {' '}
                                                    <OutOfStateBadge label={outOfState} />
                                                </>
                                            ) : null}
                                        </Text>
                                    )
                                })}
                            </Stack>
                        </ScrollArea.Autosize>
                        {saveError ? <Alert color="red">{saveError}</Alert> : null}
                        <Group justify="flex-end">
                            <Button
                                variant="default"
                                onClick={() => setPending(null)}
                                disabled={createJobs.isPending}
                            >
                                Back
                            </Button>
                            <Button
                                loading={createJobs.isPending}
                                onClick={() => void handleCreate(pending)}
                            >
                                Create {pending.length} {pending.length === 1 ? 'job' : 'jobs'}
                            </Button>
                        </Group>
                    </Stack>
                ) : null}
            </Modal>
        </Stack>
    )
}
